/**
 * Shared components and utilities for audit testing pages.
 */

import { Badge } from "@a7y/tailwind-catalyst";
import type { AuditTestRun } from "@a7y/types";
import type { SkipCacheOptions } from "@a7y/ui/audit/shared/CacheOptionsPanel";
import {
  Apple,
  CheckCircle2,
  Loader2,
  Smartphone,
  XCircle,
} from "lucide-react";

// =============================================================================
// MARK: Utilities
// =============================================================================

/**
 * Convert SkipCacheOptions (from CacheOptionsPanel) to the shape the API expects.
 * AuditSkipCacheOptions uses the same keys but all are optional booleans.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function toApiCacheOptions(opts: SkipCacheOptions): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(opts)) {
    if (value) result[key] = true;
  }
  return result;
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatDuration(ms: number | undefined): string {
  if (!ms) return "-";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  return `${mins}m ${remainSecs}s`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatElapsed(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  return formatDuration(now - start);
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

// =============================================================================
// MARK: Components
// =============================================================================

export function StatusBadge({ status }: { status: AuditTestRun["status"] }) {
  switch (status) {
    case "passed":
      return (
        <Badge color="green">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Passed
        </Badge>
      );
    case "failed":
      return (
        <Badge color="red">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      );
    case "running":
      return (
        <Badge color="blue">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Running
        </Badge>
      );
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
}

export function PlatformBadge({ platform }: { platform: string }) {
  if (platform === "appstore") {
    return (
      <Badge color="zinc">
        <Apple className="mr-1 h-3 w-3" />
        iOS
      </Badge>
    );
  }
  return (
    <Badge color="green">
      <Smartphone className="mr-1 h-3 w-3" />
      Android
    </Badge>
  );
}

export function PlatformIcon({ platform }: { platform: string }) {
  if (platform === "appstore") {
    return <Apple className="h-4 w-4 text-zinc-500" />;
  }
  return <Smartphone className="h-4 w-4 text-green-500" />;
}
